import React, { useEffect, useMemo, useState } from "react";
import { Button, Surface, Tabs } from "@heroui/react";
import { LoadingState } from "../components/LoadingState";
import { PortalFormSaveBar } from "../components/PortalFormSaveBar";
import { PortalCheckbox } from "../components/form/PortalCheckbox";
import { PortalColorPicker } from "../components/form/PortalColorPicker";
import { PortalSelect } from "../components/form/PortalSelect";
import { PortalTextField } from "../components/form/PortalTextField";
import { PortalTextArea } from "../components/form/PortalTextArea";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { isFormDirty } from "../lib/formDirty";
import { showErrorToast, showSuccessToast } from "../lib/toast";

const DEFAULT_SETTINGS = {
  auto_close_days: 30,
  allow_partial_returns: true,
  require_order_number: true,
  reason_taxonomy: ["Damaged", "Wrong item", "Changed mind"]
};

const DEFAULT_EMBED_APPEARANCE = {
  background: "#0f1a2d",
  text: "#eef3ff",
  primary: "#8BC4FF",
  accent: "#1A243A",
  surface: "#111c31",
  border: "#2b3a58",
  muted: "#aeb8d6",
  font_family: "system",
  layout: "contained",
  max_width: 720,
  inherit_theme: true
};

const EMBED_LAYOUT_OPTIONS = [
  { id: "contained", labelKey: "settings.embedLayoutContained" },
  { id: "full_bleed", labelKey: "settings.embedLayoutFullBleed" }
];

const EMBED_FONT_OPTIONS = [
  { id: "system", labelKey: "settings.embedFontSystem" },
  { id: "inter", labelKey: "settings.embedFontInter" },
  { id: "arial", labelKey: "settings.embedFontArial" },
  { id: "georgia", labelKey: "settings.embedFontGeorgia" },
  { id: "times", labelKey: "settings.embedFontTimes" },
  { id: "courier", labelKey: "settings.embedFontCourier" }
];

const EMBED_COLOR_FIELDS = [
  { key: "background", labelKey: "settings.embedColorBackground" },
  { key: "text", labelKey: "settings.embedColorText" },
  { key: "primary", labelKey: "settings.embedColorPrimary" },
  { key: "accent", labelKey: "settings.embedColorAccent" },
  { key: "surface", labelKey: "settings.embedColorSurface" },
  { key: "border", labelKey: "settings.embedColorBorder" },
  { key: "muted", labelKey: "settings.embedColorMuted" }
];

const STOREFRONT_LOCALE_OPTIONS = [
  { id: "en", labelKey: "settings.localeEn" },
  { id: "de", labelKey: "settings.localeDe" },
  { id: "fr", labelKey: "settings.localeFr" },
  { id: "es", labelKey: "settings.localeEs" },
  { id: "it", labelKey: "settings.localeIt" }
];

export function SettingsPage() {
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [formState, setFormState] = useState({
    default_locale: "en",
    notification_email: "",
    embed_appearance: { ...DEFAULT_EMBED_APPEARANCE },
    ...DEFAULT_SETTINGS
  });
  const [initialState, setInitialState] = useState(formState);
  const [embedToken, setEmbedToken] = useState("");

  useEffect(() => {
    let cancelled = false;

    apiFetch("/api/settings")
      .then((data) => {
        if (cancelled) return;
        const next = {
          default_locale: data.shop?.default_locale || "en",
          notification_email: data.shop?.notification_email || "",
          auto_close_days: data.shop?.settings?.auto_close_days ?? DEFAULT_SETTINGS.auto_close_days,
          allow_partial_returns: data.shop?.settings?.allow_partial_returns ?? DEFAULT_SETTINGS.allow_partial_returns,
          require_order_number: data.shop?.settings?.require_order_number ?? DEFAULT_SETTINGS.require_order_number,
          reason_taxonomy: data.shop?.settings?.reason_taxonomy ?? DEFAULT_SETTINGS.reason_taxonomy,
          embed_appearance: {
            ...DEFAULT_EMBED_APPEARANCE,
            ...(data.shop?.settings?.embed_appearance || {})
          }
        };
        setFormState(next);
        setInitialState(next);
      })
      .catch(() => {
        if (!cancelled) showErrorToast(t("settings.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const reasonsText = useMemo(() => formState.reason_taxonomy.join("\n"), [formState.reason_taxonomy]);
  const isDirty = useMemo(() => isFormDirty(formState, initialState), [formState, initialState]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/settings", {
        method: "PATCH",
        body: JSON.stringify({
          default_locale: formState.default_locale,
          notification_email: formState.notification_email,
          settings: {
            auto_close_days: Number(formState.auto_close_days),
            allow_partial_returns: Boolean(formState.allow_partial_returns),
            require_order_number: Boolean(formState.require_order_number),
            reason_taxonomy: formState.reason_taxonomy,
            embed_appearance: formState.embed_appearance
          }
        })
      });
      setInitialState(formState);
      showSuccessToast(t("settings.saved"));
    } catch (error) {
      if (error?.code === "billing_required") {
        showErrorToast(t("settings.billingRequired"));
      } else {
        showErrorToast(t("settings.saveError"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setFormState(initialState);

  const regenerateToken = async () => {
    try {
      const data = await apiFetch("/api/portal/embed_token/regenerate", { method: "POST" });
      setEmbedToken(data.embed_token || "");
      showSuccessToast(t("settings.embedTokenRegenerated") || "Embed token regenerated.");
    } catch {
      showErrorToast(t("settings.saveError"));
    }
  };

  if (loading) return <LoadingState label={t("common.loadingSettings")} />;

  return (
    <>
      <PortalFormSaveBar isDirty={isDirty} onSave={handleSave} onDiscard={handleDiscard} saving={saving} />

      <div className="portal-withdrawals-layout">
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(String(key))}
          orientation="vertical"
          className="portal-status-tabs w-full max-w-[220px]"
        >
          <Tabs.ListContainer>
            <Tabs.List
              aria-label={t("settings.pageTitle")}
              className="portal-status-tabs__list w-full *:w-full *:justify-start *:px-3 *:py-2 *:text-sm *:font-medium *:data-[selected=true]:text-accent-foreground"
            >
              <Tabs.Tab id="general">
                {t("settings.pageTitle")}
                <Tabs.Indicator className="bg-accent" />
              </Tabs.Tab>
              <Tabs.Tab id="embed">
                {t("settings.embedTokenTitle") || "Embed token"}
                <Tabs.Indicator className="bg-accent" />
              </Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>

        <div className="portal-withdrawals-layout__content">
          {activeTab === "general" ? (
            <Surface className="portal-page-surface">
              <h2 className="portal-panel__title">{t("settings.pageTitle")}</h2>
              <h3 className="text-base font-semibold">{t("settings.notificationRules")}</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <PortalTextField
                  label={t("settings.notificationEmail")}
                  type="email"
                  value={formState.notification_email}
                  onChange={(v) => setFormState((prev) => ({ ...prev, notification_email: v }))}
                />
                <div className="portal-field">
                  <PortalSelect
                    label={t("settings.defaultLocale")}
                    value={formState.default_locale}
                    onChange={(v) => setFormState((prev) => ({ ...prev, default_locale: v }))}
                    options={STOREFRONT_LOCALE_OPTIONS.map((opt) => ({ id: opt.id, label: t(opt.labelKey) }))}
                  />
                  <p className="portal-field__hint">{t("settings.defaultLocaleHelp")}</p>
                </div>
              </div>

              <h3 className="text-base font-semibold">{t("settings.retentionControls")}</h3>
              <PortalTextField
                label={t("settings.autoCloseDays")}
                type="number"
                value={String(formState.auto_close_days)}
                onChange={(v) =>
                  setFormState((prev) => ({
                    ...prev,
                    auto_close_days: Number(v || DEFAULT_SETTINGS.auto_close_days)
                  }))
                }
              />
              <div className="portal-checkbox-row">
                <PortalCheckbox
                  isSelected={formState.allow_partial_returns}
                  onChange={(checked) => setFormState((prev) => ({ ...prev, allow_partial_returns: checked }))}
                >
                  {t("settings.allowPartialReturns")}
                </PortalCheckbox>
              </div>
              <div className="portal-checkbox-row">
                <PortalCheckbox
                  isSelected={formState.require_order_number}
                  onChange={(checked) => setFormState((prev) => ({ ...prev, require_order_number: checked }))}
                >
                  {t("settings.requireOrderNumber")}
                </PortalCheckbox>
              </div>

              <h3 className="text-base font-semibold">{t("settings.reasonTaxonomy")}</h3>
              <PortalTextArea
                label={t("settings.reasonTaxonomyHelp")}
                value={reasonsText}
                onChange={(v) =>
                  setFormState((prev) => ({
                    ...prev,
                    reason_taxonomy: v
                      .split("\n")
                      .map((value) => value.trim())
                      .filter(Boolean)
                  }))
                }
              />
            </Surface>
          ) : (
            <Surface className="portal-page-surface">
              <p className="portal-panel__description">
                {t("settings.embedDefaultLocaleNote", { locale: formState.default_locale })}
              </p>

              <h3 className="portal-panel__section-title">{t("settings.embedTokenTitle") || "Embed token"}</h3>
              <p className="portal-panel__description">
                {t("settings.embedTokenHelp") || "Regenerate your standalone embed token."}
              </p>
              <Button variant="secondary" onPress={regenerateToken}>
                {t("settings.regenerateEmbedToken") || "Regenerate embed token"}
              </Button>
              {embedToken ? <code className="portal-code">{embedToken}</code> : null}

              <h3 className="portal-panel__section-title portal-panel__section-title--spaced">{t("settings.embedAppearanceTitle")}</h3>
              <p className="portal-panel__description">{t("settings.embedAppearanceHelp")}</p>

              <div className="portal-embed-appearance-controls">
                <PortalSelect
                  label={t("settings.embedLayout")}
                  value={formState.embed_appearance.layout || "contained"}
                  onChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      embed_appearance: {
                        ...prev.embed_appearance,
                        layout: value || "contained"
                      }
                    }))
                  }
                  options={EMBED_LAYOUT_OPTIONS.map((opt) => ({ id: opt.id, label: t(opt.labelKey) }))}
                />
                <PortalTextField
                  label={t("settings.embedMaxWidth")}
                  value={String(formState.embed_appearance.max_width || 720)}
                  onChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      embed_appearance: {
                        ...prev.embed_appearance,
                        max_width: Number(value || 720)
                      }
                    }))
                  }
                />
                <div className="portal-checkbox-row">
                  <PortalCheckbox
                    isSelected={formState.embed_appearance.inherit_theme !== false}
                    onChange={(checked) =>
                      setFormState((prev) => ({
                        ...prev,
                        embed_appearance: {
                          ...prev.embed_appearance,
                          inherit_theme: Boolean(checked)
                        }
                      }))
                    }
                  >
                    {t("settings.embedInheritTheme")}
                  </PortalCheckbox>
                </div>
                <PortalSelect
                  label={t("settings.embedFontFamily")}
                  value={formState.embed_appearance.font_family || "system"}
                  onChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      embed_appearance: {
                        ...prev.embed_appearance,
                        font_family: value || "system"
                      }
                    }))
                  }
                  options={EMBED_FONT_OPTIONS.map((opt) => ({ id: opt.id, label: t(opt.labelKey) }))}
                />
              </div>

              <div className="portal-embed-appearance-grid">
                {EMBED_COLOR_FIELDS.map(({ key, labelKey }) => (
                  <PortalColorPicker
                    key={key}
                    label={t(labelKey)}
                    value={formState.embed_appearance[key] || DEFAULT_EMBED_APPEARANCE[key]}
                    onChange={(color) =>
                      setFormState((prev) => ({
                        ...prev,
                        embed_appearance: {
                          ...prev.embed_appearance,
                          [key]: color
                        }
                      }))
                    }
                  />
                ))}
              </div>
            </Surface>
          )}
        </div>
      </div>
    </>
  );
}
